import { css } from 'lit';
import { CSSResultArray, LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from '@/utils/connect';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import store from '../../redux/redux-store';
import resetScss from '../css/lib/reset.scss';
import sharedScss from '../css/shared.scss';
import scss from '../../utils/scssAsCss';
import '../common/notification-banner';
import utilityClassesCss from '../css/utilityClasses.css';
import remEventFormStyles from '../rem/rem-event-form-styles';
import { sendPasswordResetEmail } from '../../redux/user-management/user-management-actions';
import { EMAIL_ADDRESS_REGEX } from '../../constants';
import dialogCss from '../css/dialog.css';

@customElement('forgot-password-dialog')
export default class ForgotPasswordDialog extends connect(store)(LitElement) {
	private dialogRef: Ref<HTMLDialogElement> = createRef();

	private inputRef: Ref<HTMLInputElement> = createRef();

	emailPasswordResetVal = '';

	@state() emailPasswordResetError?: string | null;

	@state() forgotPassLoading = false;

	public static override get styles(): CSSResultArray {
		return [
			resetScss,
			sharedScss,
			utilityClassesCss,
			remEventFormStyles,
			dialogCss,
			css`
.forgot-password-btn {
  color: #bfbfbf;
  text-align: left;
}

.pb-2 {
  padding-bottom: calc(var(--ggutter) * 0.5);
}
`,
		];
	}

	public override render(): TemplateResult {
		return html`
			<button class="btn-base forgot-password-btn" @click=${this.onForgotPassClick}>
				Forgot Password?
			</button>
			<dialog ${ref(this.dialogRef)} class="dialog" @click=${this.onForgotPassDialogCloseBackdrop}>
				<form @submit=${this.onForgotPassDialogSubmit} class="dialog-form dialog-form-sm">
					<h2 class="dialog-h2 pb-2">Forgot your password?</h2>

					<label class="field-label flex col">
						<p class="dialog-font-size-sm pb-2">Enter your email address to reset your password.</p>
						<input
							${ref(this.inputRef)}
							name="email-password-reset"
							class="field-box"
							@input=${this.onEmailInput}
							@blur=${this.onEmailBlur}
							placeholder="user@example.com"
						/>
						${this.emailPasswordResetError
							? html`<p class="field-error-msg">${this.emailPasswordResetError}</p>`
							: nothing}
					</label>

					<div class="flex justify-content-end dialog-pt-6">
						<button class="btn btn-lg" type="button" @click=${this.onForgotPassDialogClose}>
							Cancel
						</button>
						<button
							type="submit"
							class="btn btn-lg btn-primary"
							?disabled=${this.forgotPassLoading}
						>
							Reset Password
						</button>
					</div>
				</form>
			</dialog>
		`;
	}

	private onEmailInput(event: InputEvent) {
		const target = event.target as HTMLInputElement;
		this.emailPasswordResetVal = target.value.trim();
	}

	private onEmailBlur() {
		this.emailPasswordResetError = null;
		if (!this.emailPasswordResetVal) {
			this.emailPasswordResetError = 'Please enter your email address';
		}
		if (!EMAIL_ADDRESS_REGEX.test(this.emailPasswordResetVal)) {
			this.emailPasswordResetError = 'Please enter a valid email address';
		}
	}

	private onForgotPassClick(): void {
		const dialog = this.dialogRef?.value as HTMLDialogElement;
		dialog.showModal();
	}

	private onForgotPassDialogCloseBackdrop(event: MouseEvent): void {
		const dialog = this.dialogRef?.value as HTMLDialogElement;
		if (dialog !== undefined && event.target === dialog) {
			dialog.close();
		}
	}

	private onForgotPassDialogClose(event: MouseEvent): void {
		event.preventDefault();
		const dialog = this.dialogRef?.value as HTMLDialogElement;
		dialog.close();
	}

	private async onForgotPassDialogSubmit(event: MouseEvent) {
		event.preventDefault();
		if (this.emailPasswordResetError) {
			this.inputRef?.value?.focus();
			return;
		}
		const dialog = this.dialogRef?.value as HTMLDialogElement;

		this.forgotPassLoading = true;
		await store.dispatch(sendPasswordResetEmail(this.emailPasswordResetVal));
		this.forgotPassLoading = false;
		dialog.close();
	}
}
